import { useBasiqSync } from '@/hooks/use-basiq-sync';
import { useSubscription } from '@/hooks/use-subscription';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Landmark, RefreshCw, Link2, Loader2, Lock, Trash2, RotateCcw, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function BankSyncSection() {
  const { subscribed, loading: subLoading } = useSubscription();
  const {
    loading,
    connections,
    basiqAccounts,
    basiqUserId,
    lastSync,
    startConnect,
    checkStatus,
    syncTransactions,
    disconnectBank,
  } = useBasiqSync();

  const [disconnectId, setDisconnectId] = useState<string | null>(null);
  const disconnectConn = connections.find(c => c.id === disconnectId);
  useEffect(() => {
    if (basiqUserId) {
      checkStatus();
    }
  }, [basiqUserId, checkStatus]);

  if (subLoading) return null;

  if (!subscribed) {
    return (
      <Card className="mb-4 border-dashed">
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
            <Lock className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm">Live Bank Sync</p>
            <p className="text-xs text-muted-foreground">
              Upgrade to Pro to connect your bank and auto-import transactions
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasConnections = connections.length > 0;

  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Bank Sync
        </h2>
        {basiqUserId && (
          <div className="flex gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={checkStatus}
              disabled={loading}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        )}
      </div>

      {hasConnections && (
        <div className="space-y-1.5">
          {connections.map((conn) => (
            <Card key={conn.id}>
              <CardContent className="flex items-center gap-3 p-3">
                {conn.institutionLogo ? (
                  <img
                    src={conn.institutionLogo}
                    alt={conn.institution}
                    className="h-8 w-8 rounded-lg object-contain"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <Landmark className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{conn.institution}</p>
                <p className="text-xs text-muted-foreground">
                    {conn.lastUpdated
                      ? `Updated ${new Date(conn.lastUpdated).toLocaleDateString('en-AU')}`
                      : conn.status === 'active' ? 'Connected' : 'Pending'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant={conn.status === 'active' ? 'default' : 'destructive'}
                    className="text-[10px]"
                  >
                    {conn.status}
                  </Badge>
                  {conn.status !== 'active' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-primary"
                      onClick={startConnect}
                      disabled={loading}
                      title="Reconnect"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => setDisconnectId(conn.id)}
                    disabled={loading}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {basiqAccounts.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {basiqAccounts.length} account{basiqAccounts.length !== 1 ? 's' : ''} linked via bank sync
        </p>
      )}

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={startConnect}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Link2 className="h-4 w-4" />
          )}
          {hasConnections ? 'Add Bank' : 'Connect Bank'}
        </Button>

        {basiqUserId && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={syncTransactions}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync Now
          </Button>
        )}
      </div>

      {lastSync && (
        <p className="text-[11px] text-muted-foreground">
          Last synced: {new Date(lastSync).toLocaleString('en-AU')}
        </p>
      )}

      <Alert className="border-muted bg-muted/40">
        <Info className="h-4 w-4" />
        <AlertTitle className="text-xs font-semibold">Having trouble connecting?</AlertTitle>
        <AlertDescription className="text-xs text-muted-foreground space-y-1">
          <p>Common issues include:</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li><strong>Connection Not Available</strong> — Your bank may not yet be supported via open banking.</li>
            <li><strong>Connections Not Enabled</strong> — The bank sync service may need to be activated on our end.</li>
            <li><strong>Connection timeout</strong> — Try again in a few minutes; your bank may be temporarily unavailable.</li>
          </ul>
          <p className="pt-1">
            If the issue persists, please{' '}
            <a
              href="mailto:inceptionglen@gmail.com?subject=Bank%20Sync%20Issue"
              className="underline text-primary font-medium"
            >
              contact support
            </a>{' '}
            and we'll help get you connected.
          </p>
        </AlertDescription>
      </Alert>

      <AlertDialog open={!!disconnectId} onOpenChange={(open) => !open && setDisconnectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {disconnectConn?.institution ?? 'bank'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the bank connection. Previously synced transactions will remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (disconnectId) {
                  await disconnectBank(disconnectId);
                  setDisconnectId(null);
                }
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
